/**
 * Report 102 (AIM Selective Menu Changes) from the AIM selective menu review dialog.
 *
 * The report reads the change set as XML in its settings (`StrDataXML`, the way the desktop serializes its data set):
 * `tblDayMealtime` rows (one per menu date and mealtime that changes) and `tblCombined` rows (each meal's items side by
 * side, `FKey` = the day-mealtime's `PKey`). The dialog holds both tables as row dictionaries with the reviewer's
 * choices applied; AIM's proposal (the rows as the engine returned them) rides along as `Proposed_Itm_Descr_New` /
 * `Proposed_Selected`, so the report can print each change as Accepted or Declined. Without a schema the report reads
 * every value as text ("true" / "false" for the flags).
 *
 * The context the report prints over the list (unit and room, the diet order change, when and by whom AIM ran) comes
 * from the run's `reportContext`; the menu date range from the day-mealtimes. Dates go out as the facility's wall
 * clock with no zone (the report reads settings with DateTimeZoneHandling.Unspecified).
 */
import type { AimSMReportContext, AimSMRowDict } from '../../api/aimSelectiveMenuApi';
import type { QueueReportRequest } from '../../api/reportApi';

/** Report 102's id (tblRS_Report). */
export const SM_CHANGES_REPORT_ID = 102;

/** The columns the report prints or links on, in the server's PascalCase. */
const DAY_MEALTIME_COLUMNS = ['PKey', 'Day', 'Mealtime', 'MenuDate', 'MenuDisplayString'];
const COMBINED_COLUMNS = [
  'PKey', 'FKey', 'Day', 'Mealtime', 'Position_New', 'Position_Old',
  'Itm_Descr_Old', 'Itm_Descr_New', 'Selected', 'IsNewMPItem',
];
/** AIM's proposal, before the review (the report's AIMSMChangeList.ProposedDescrColumn / ProposedSelectedColumn). */
const PROPOSED_DESCR = 'Proposed_Itm_Descr_New';
const PROPOSED_SELECTED = 'Proposed_Selected';

function escapeXml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;');
}

/** The row's value for a server column, matching the key case-insensitively (the API lower-cases first letters). */
function field(row: AimSMRowDict | undefined, column: string): unknown {
  if (!row) return undefined;
  if (Object.prototype.hasOwnProperty.call(row, column)) return row[column];
  const lc = column.toLowerCase();
  const key = Object.keys(row).find((k) => k.toLowerCase() === lc);
  return key === undefined ? undefined : row[key];
}

function xmlValue(v: unknown): string {
  return escapeXml(typeof v === 'boolean' ? (v ? 'true' : 'false') : String(v));
}

function rowXml(table: string, row: AimSMRowDict, columns: string[], extra?: Array<[string, unknown]>): string {
  let xml = `<${table}>`;
  for (const c of columns) {
    const v = field(row, c);
    if (v === undefined || v === null || v === '') continue;
    xml += `<${c}>${xmlValue(v)}</${c}>`;
  }
  for (const [c, v] of extra ?? []) {
    if (v === undefined || v === null) continue;
    xml += `<${c}>${xmlValue(v)}</${c}>`;
  }
  return `${xml}</${table}>`;
}

const isTrue = (v: unknown): boolean => v === true || v === 1 || v === '1' || v === 'true';

/**
 * The change set as the DataSet XML report 102 reads. `proposal` is tblCombined as the engine returned it (before the
 * review), matched to `combined` by PKey (else by position); without it the rows as they stand are the proposal.
 */
export function buildSmChangesXml(
  dayMealtimes: AimSMRowDict[],
  combined: AimSMRowDict[],
  proposal?: AimSMRowDict[],
): string {
  const byPKey = new Map<string, AimSMRowDict>();
  for (const p of proposal ?? []) {
    const k = field(p, 'PKey');
    if (k !== undefined && k !== null && !byPKey.has(String(k))) byPKey.set(String(k), p);
  }
  return '<NewDataSet>'
    + dayMealtimes.map((r) => rowXml('tblDayMealtime', r, DAY_MEALTIME_COLUMNS)).join('')
    + combined.map((r, i) => {
      const k = field(r, 'PKey');
      const p = (k !== undefined && k !== null ? byPKey.get(String(k)) : undefined) ?? proposal?.[i] ?? r;
      const descr = field(p, 'Itm_Descr_New');
      return rowXml('tblCombined', r, COMBINED_COLUMNS, [
        [PROPOSED_DESCR, descr === undefined || descr === null ? '' : descr],
        [PROPOSED_SELECTED, isTrue(field(p, 'Selected'))],
      ]);
    }).join('')
    + '</NewDataSet>';
}

/** "yyyy-MM-ddT00:00:00" of a row date (the server ships "2026-10-17T00:00:00"), or null. */
function dateOnly(v: unknown): string | null {
  if (v === undefined || v === null || v === '') return null;
  const m = /^(\d{4}-\d{2}-\d{2})/.exec(String(v));
  return m ? `${m[1]}T00:00:00` : null;
}

/** The first and last menu date of the change set. */
export function menuDateRange(dayMealtimes: AimSMRowDict[]): { start: string | null; end: string | null } {
  const dates = dayMealtimes.map((r) => dateOnly(field(r, 'MenuDate'))).filter((d): d is string => d !== null).sort();
  return { start: dates[0] ?? null, end: dates[dates.length - 1] ?? null };
}

/** A context date as the report reads it: the wall clock, no zone (a zone or a "Z" is cut off). */
function wallClock(v: string | null | undefined): string | null {
  if (!v) return null;
  const m = /^(\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(?::\d{2})?)/.exec(v);
  if (m) return m[1].length === 16 ? `${m[1]}:00` : m[1];
  return dateOnly(v);
}

/** The queue request for report 102: AIMSMChangesSettings with no saved grid layouts (the fixed layout prints). */
export function smChangesReportRequest(args: {
  facilityId: number;
  patientId: number;
  patientFullName: string;
  userId: number;
  dateFor: string;
  dayMealtimes: AimSMRowDict[];
  combined: AimSMRowDict[];
  /** tblCombined as the engine returned it, before the review. */
  proposal?: AimSMRowDict[];
  reportContext?: AimSMReportContext | null;
}): QueueReportRequest {
  const ctx = args.reportContext ?? {};
  const range = menuDateRange(args.dayMealtimes);
  return {
    reportType: 'AIMSMChanges',
    fKey_User: args.userId,
    fKey_RS_Report: SM_CHANGES_REPORT_ID,
    settingsJson: JSON.stringify({
      fKey_Facility: args.facilityId,
      fKey_Patient: args.patientId,
      dateFor: args.dateFor,
      residentFullName: args.patientFullName,
      strMasterLayout: '',
      strDetailLayout: '',
      strDataLayout: '',
      strDataXML: buildSmChangesXml(args.dayMealtimes, args.combined, args.proposal),
      strDataXMLSchema: '',
      residentUnit: ctx.residentUnit ?? '',
      residentRoom: ctx.residentRoom ?? '',
      dietOrderOld: ctx.dietOrderOld ?? '',
      dietOrderNew: ctx.dietOrderNew ?? '',
      dietOrderEffectiveDate: wallClock(ctx.dietOrderEffectiveDate),
      aimRunDateTime: wallClock(ctx.aimRunDateTime),
      aimRunBy: ctx.aimRunBy ?? '',
      menuDateStart: range.start,
      menuDateEnd: range.end,
    }),
    reportName: 'Selective Menu Changes',
    dateFor: args.dateFor,
  };
}
